/**
 * shapeSampling - Serialisierte Fabric-Objekte → Polygon-Punkte in Metern (#600)
 *
 * Reine Logik, keine fabric-/three-Imports: arbeitet auf dem Snapshot aus
 * serializeCanvasObjects() (Fabric-JSON). Repliziert die Transform-Semantik
 * von fabric (calcTransformMatrix ohne Skew) für serialisierte Objekte:
 *
 *   world = translate(center) · rotate(angle) · scale(scaleX·flip, scaleY·flip)
 *
 * - center wird aus left/top + originX/Y + skalierten Dimensionen (inkl.
 *   strokeWidth wie fabric _getTransformedDimensions) abgeleitet
 * - Polygon-/Pfad-Punkte sind bbox-relativ → pathOffset (bbox-Zentrum der
 *   Rohpunkte, ohne Stroke) wird abgezogen — Semantik wie gateLinkUtils
 * - Linien: x1..y2 sind nach Moves potenziell stale (#611-Befund) — nur
 *   Richtung/Länge werden daraus gelesen, die Position kommt aus left/top
 *   (entspricht fabric calcLinePoints + Transform)
 *
 * Fehlende originX/Y bedeuten fabric-v7-Default 'center' (Snapshots kommen
 * immer vom live v7-Canvas; v6-Altobjekte tragen explizite Origins, #425).
 */

import { pxToM } from './units';

export const ELLIPSE_SEGMENTS = 32;
export const BEZIER_SAMPLES_PER_SEGMENT = 12;

const DEG_TO_RAD = Math.PI / 180;

const ORIGIN_FRACTIONS = { left: 0, top: 0, center: 0.5, right: 1, bottom: 1 };

function originFraction(value) {
  if (typeof value === 'number') return value;
  return ORIGIN_FRACTIONS[value] ?? 0.5;
}

/**
 * Scaled outer dimensions incl. strokeWidth (fabric _getTransformedDimensions).
 */
function transformedDimensions(obj) {
  const width = obj.width ?? 0;
  const height = obj.height ?? 0;
  const scaleX = Math.abs(obj.scaleX ?? 1);
  const scaleY = Math.abs(obj.scaleY ?? 1);
  const stroke = obj.strokeWidth ?? 0;
  if (obj.strokeUniform) {
    return { x: width * scaleX + stroke, y: height * scaleY + stroke };
  }
  return { x: (width + stroke) * scaleX, y: (height + stroke) * scaleY };
}

/**
 * Compose the object's world matrix [a, b, c, d, e, f] from serialized props.
 * @param {object} obj - Serialized fabric object
 * @returns {number[]} 2D affine matrix (fabric layout)
 */
export function composeMatrix(obj) {
  const angleRad = (obj.angle ?? 0) * DEG_TO_RAD;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const sx = (obj.scaleX ?? 1) * (obj.flipX ? -1 : 1);
  const sy = (obj.scaleY ?? 1) * (obj.flipY ? -1 : 1);

  // Center aus Positionspunkt (left/top @ origin) + rotiertem Origin→Center-Vektor
  const dims = transformedDimensions(obj);
  const dx = (0.5 - originFraction(obj.originX)) * dims.x;
  const dy = (0.5 - originFraction(obj.originY)) * dims.y;
  const cx = (obj.left ?? 0) + dx * cos - dy * sin;
  const cy = (obj.top ?? 0) + dx * sin + dy * cos;

  return [cos * sx, sin * sx, -sin * sy, cos * sy, cx, cy];
}

/** m1 · m2 (fabric multiplyTransformMatrices). */
export function multiplyMatrices(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

/** Apply matrix to a point. */
export function applyMatrix(m, p) {
  return {
    x: m[0] * p.x + m[2] * p.y + m[4],
    y: m[1] * p.x + m[3] * p.y + m[5],
  };
}

// ---------------------------------------------------------------------------
// Lokale (center-relative) Konturen pro Fabric-Typ
// ---------------------------------------------------------------------------

function rectLocalPoints(obj) {
  const w = (obj.width ?? 0) / 2;
  const h = (obj.height ?? 0) / 2;
  return [
    { x: -w, y: -h },
    { x: w, y: -h },
    { x: w, y: h },
    { x: -w, y: h },
  ];
}

function ellipseLocalPoints(obj) {
  const rx = obj.rx ?? obj.radius ?? 0;
  const ry = obj.ry ?? obj.radius ?? 0;
  if (rx <= 0 || ry <= 0) return null;
  const pts = [];
  for (let i = 0; i < ELLIPSE_SEGMENTS; i++) {
    const theta = (i / ELLIPSE_SEGMENTS) * Math.PI * 2;
    pts.push({ x: rx * Math.cos(theta), y: ry * Math.sin(theta) });
  }
  return pts;
}

function polygonLocalPoints(obj) {
  const points = obj.points || [];
  if (points.length < 2) return null;
  // pathOffset = bbox-Zentrum der Rohpunkte (fabric _calcDimensions, ohne Stroke)
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const offX = minX + (maxX - minX) / 2;
  const offY = minY + (maxY - minY) / 2;
  return points.map((p) => ({ x: p.x - offX, y: p.y - offY }));
}

/**
 * Fabric Line: Richtung/Länge aus den (ggf. stalen) Rohkoordinaten, Position
 * aus left/top — entspricht calcLinePoints() + Transform (#611-Befund).
 */
function lineLocalPoints(obj) {
  const { x1 = 0, y1 = 0, x2 = 0, y2 = 0 } = obj;
  const w = Math.abs(x2 - x1);
  const h = Math.abs(y2 - y1);
  const lx1 = x1 <= x2 ? -w / 2 : w / 2;
  const ly1 = y1 <= y2 ? -h / 2 : h / 2;
  return [
    { x: lx1, y: ly1 },
    { x: -lx1, y: -ly1 },
  ];
}

// --- Bezier-Pfad (M/L/C/Q/Z, absolute Kommandos wie von fabric normalisiert) ---

function cubicPoint(p0, p1, p2, p3, t) {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

function quadPoint(p0, p1, p2, t) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x,
    y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y,
  };
}

/**
 * Sample an absolute-command fabric path into a polyline (path space).
 * Unbekannte Kommandos werden übersprungen (degradieren, kein Crash).
 */
export function samplePathCommands(path, samplesPerSegment = BEZIER_SAMPLES_PER_SEGMENT) {
  const pts = [];
  let current = null;
  let closed = false;

  for (const cmd of path || []) {
    const op = cmd[0];
    if (op === 'M') {
      current = { x: cmd[1], y: cmd[2] };
      pts.push(current);
    } else if (op === 'L' && current) {
      current = { x: cmd[1], y: cmd[2] };
      pts.push(current);
    } else if (op === 'C' && current) {
      const p1 = { x: cmd[1], y: cmd[2] };
      const p2 = { x: cmd[3], y: cmd[4] };
      const p3 = { x: cmd[5], y: cmd[6] };
      for (let i = 1; i <= samplesPerSegment; i++) {
        pts.push(cubicPoint(current, p1, p2, p3, i / samplesPerSegment));
      }
      current = p3;
    } else if (op === 'Q' && current) {
      const p1 = { x: cmd[1], y: cmd[2] };
      const p2 = { x: cmd[3], y: cmd[4] };
      for (let i = 1; i <= samplesPerSegment; i++) {
        pts.push(quadPoint(current, p1, p2, i / samplesPerSegment));
      }
      current = p2;
    } else if (op === 'Z' || op === 'z') {
      closed = true;
    }
  }
  return { points: pts, closed };
}

function pathLocalPoints(obj) {
  const { points, closed } = samplePathCommands(obj.path);
  if (points.length < 2) return null;
  // pathOffset aus der Sample-bbox (dicht genug für Garten-Maßstab)
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const offX = minX + (maxX - minX) / 2;
  const offY = minY + (maxY - minY) / 2;
  return {
    points: points.map((p) => ({ x: p.x - offX, y: p.y - offY })),
    closed,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Sample a serialized fabric object into world-space ground points (Meter).
 *
 * @param {object} obj - Serialized fabric object (snapshot entry)
 * @param {object} [options]
 * @param {number} [options.pixelsPerCm=1]
 * @param {number[]} [options.parentMatrix] - Gruppen-Transform (Kinder sind center-relativ)
 * @returns {{ points: Array<{x,z}>, closed: boolean }|null} null = nicht abbildbar
 */
export function sampleObjectOutline(obj, { pixelsPerCm = 1, parentMatrix = null } = {}) {
  const type = (obj?.type || '').toLowerCase();

  let local = null;
  let closed = true;

  if (type === 'rect') {
    local = rectLocalPoints(obj);
  } else if (type === 'ellipse' || type === 'circle') {
    local = ellipseLocalPoints(obj);
  } else if (type === 'polygon' || type === 'polyline') {
    local = polygonLocalPoints(obj);
    closed = type === 'polygon';
  } else if (type === 'line') {
    local = lineLocalPoints(obj);
    closed = false;
  } else if (type === 'path') {
    const sampled = pathLocalPoints(obj);
    if (sampled) {
      local = sampled.points;
      closed = sampled.closed;
    }
  }

  if (!local || local.length < 2) return null;

  let m = composeMatrix(obj);
  if (parentMatrix) m = multiplyMatrices(parentMatrix, m);

  const points = local.map((p) => {
    const w = applyMatrix(m, p);
    return { x: pxToM(w.x, pixelsPerCm), z: pxToM(w.y, pixelsPerCm) };
  });
  return { points, closed };
}

/**
 * Expand an open polyline (Meter) into a closed band polygon of given width —
 * für Linien-Typen (Zaun, Mauer als Linie, Rasenkante).
 *
 * @param {Array<{x,z}>} points - Polyline in Metern (≥ 2 Punkte)
 * @param {number} widthM - Bandbreite in Metern
 * @returns {Array<{x,z}>|null} geschlossenes Polygon (links-Seite + rechts-Seite rückwärts)
 */
export function expandPolylineToBand(points, widthM) {
  if (!points || points.length < 2 || widthM <= 0) return null;
  const half = widthM / 2;

  // Normale pro Punkt: Mittel der angrenzenden Segment-Normalen
  const normals = points.map((p, i) => {
    const prev = points[Math.max(i - 1, 0)];
    const next = points[Math.min(i + 1, points.length - 1)];
    const dx = next.x - prev.x;
    const dz = next.z - prev.z;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    return { x: -dz / len, z: dx / len };
  });

  const left = points.map((p, i) => ({
    x: p.x + normals[i].x * half,
    z: p.z + normals[i].z * half,
  }));
  const right = points.map((p, i) => ({
    x: p.x - normals[i].x * half,
    z: p.z - normals[i].z * half,
  }));
  return [...left, ...right.reverse()];
}

/**
 * Zentroid + Fläche eines Polygons (Meter) — für Degenerationsprüfungen.
 */
export function polygonArea(points) {
  if (!points || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.z - b.x * a.z;
  }
  return Math.abs(area) / 2;
}
